import { useGSAP } from '@gsap/react'
import { PortableText } from '@portabletext/react'
import { gsap } from 'gsap'
import { useRef } from 'react'

import nestedLinesSplit from '~/lib/nestedLinesSplit'
import SplitText from '~/lib/SplitText'

import useMediaQuery from './hooks/useMediaQuery'

declare module 'SplitText' {
  export interface SplitText {
    revert(): void
  }
}

gsap.registerPlugin(SplitText)

interface PortableTextScrollAnimationProps {
  value: any
  start?: string
  end?: string
}

const PortableTextScrollAnimation = ({
  start = 'top bottom-=10%',
  end = 'bottom top-=10%',
  value,
}: PortableTextScrollAnimationProps) => {
  const isMobile = useMediaQuery('(max-width: 1024px)')

  if (isMobile) {
    return <PortableTextScrollAnimationMobile value={value} />
  } else {
    return (
      <PortableTextScrollAnimationDesktop
        value={value}
        start={start}
        end={end}
      />
    )
  }
}

const PortableTextScrollAnimationMobile = ({ value }: { value: any }) => (
  <PortableText value={value} />
)

const PortableTextScrollAnimationDesktop = ({
  start = 'top bottom',
  end = 'bottom top',
  value,
}: PortableTextScrollAnimationProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useGSAP(() => {
    const element = ref.current
    if (element) {
      const split = nestedLinesSplit(element.querySelectorAll('p'), {
        type: 'lines',
      })
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start,
          end,
          scrub: true,
        },
      })

      tl.fromTo(
        split.lines,
        {
          y: 30,
          '-webkit-filter': `blur(${10}px)`,
          filter: `blur(${10}px)`,
          skewY: 3,
          opacity: 0,
        },
        {
          y: 0,
          '-webkit-filter': `blur(${0}px)`,
          filter: `blur(${0}px)`,
          skewY: 0,
          opacity: 1,
          duration: 1.5,
          stagger: 0.19,
          delay: 0.2,
          ease: 'expo.inOut',
        },
      ).to(split.lines, {
        y: -30,
        '-webkit-filter': `blur(${10}px)`,
        filter: `blur(${10}px)`,
        skewY: -2,
        opacity: 0,
        duration: 2.5,
        stagger: 0.19,
        delay: 0.2,
        ease: 'expo.inOut',
      })
    }
  }, [])

  return (
    <div ref={ref}>
      <PortableText value={value} />
    </div>
  )
}

export default PortableTextScrollAnimation

interface TextScrollAnimationProps {
  text: string
  start?: string
  end?: string
}

const TextScrollAnimation = ({
  text,
  start = 'top bottom',
  end = 'bottom top',
}: TextScrollAnimationProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useGSAP(() => {
    const element = ref.current
    if (element) {
      const split = new SplitText(element, { type: 'lines' })
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start,
          end,
          scrub: true,
        },
      })

      tl.fromTo(
        split.lines,
        {
          y: 30,
          '-webkit-filter': `blur(${10}px)`,
          filter: `blur(${10}px)`,
          skewY: 3,
          opacity: 0,
        },
        {
          y: 0,
          '-webkit-filter': `blur(${0}px)`,
          filter: `blur(${0}px)`,
          skewY: 0,
          opacity: 1,
          duration: 1.5,
          stagger: 0.19,
          delay: 0.2,
          ease: 'expo.inOut',
        },
      ).to(split.lines, {
        y: -30,
        '-webkit-filter': `blur(${10}px)`,
        filter: `blur(${10}px)`,
        skewY: -2,
        opacity: 0,
        duration: 2.5,
        stagger: 0.19,
        delay: 0.2,
        ease: 'expo.inOut',
      })
    }
  }, [])

  return <div ref={ref}>{text}</div>
}

export { TextScrollAnimation }
